import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

import Staff from "../Staff/Staff";
import Roles from "../Roles/Roles";
import Trucks from "../Trucks/Trucks";
import Locations from "../Locations/Locations";
import LocationGroups from "../LocationGroups/LocationGroups";
import classNames from "../../utils/classNames";
import AuthConnections from "./AuthConnections";
import BoxSizeEditor from "../BoxSizeEditor/BoxSizeEditor";
import AdminCredentials from "../AdminCredentials/AdminCredentials";
import CheckInEditor from "../CheckInEditor/CheckInEditor";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import WarehouseLocations from "../WarehouseLocations/WarehouseLocations";
import Sites from "../Sites/Sites";
import PartBins from "../PartBins/PartBins";
import PartAssemblyEditor from "../PartAssemblyEditor/PartAssemblyEditor";
import PartCategorySubParts from "../PartCategorySubParts/PartCategorySubParts";
import WarrantyManager from "../WarrantyManager/WarrantyManager";
import EndSlateEditor from "../EndSlate/EndSlateEditor";
import ShippingPolicies from "../ShippingPolicies/ShippingPolicies";
import TemplatePolicies from "../TemplatePolicies/TemplatePolicies";
import AdminShippingProviders from "../AdminShippingProviders/AdminShippingProviders";
import ListingRules from "../ListingRules/ListingRules";
import ListingRestrictions from "../ListingRestrictions/ListingRestrictions";
import RelistingRules from "../RelistingRules/RelistingRules";
import PartImageSettingsEditor from "./PartImageSettings/PartImageSettingsEditor";
import StockNumberFormatEditor from "./StockNumberFormat/StockNumberFormatEditor";
import VatMarginSchemeSettings from "./VatMarginScheme/VatMarginSchemeSettings";
import PartHistoryFieldsEditor from "./PartHistoryFields/PartHistoryFieldsEditor";
import PartTagEditor from "./PartTagEditor/PartTagEditor";
import usePartHistoryEnabled from "../../hooks/usePartHistoryEnabled";
import TemplateEditor from "../TemplateEditor/TemplateEditor";
import ShopifyShippingPolicySync from "../ShopifyShippingPolicySync/ShopifyShippingPolicySync";
import MaterialManager from "../MaterialManager/MaterialManager";
import DepollutionEditor from "../DepollutionEditor/DepollutionEditor";
import DailyChecksEditor from "../DailyChecksEditor/DailyChecksEditor";
import DailyChecksResults from "../DailyChecks/DailyChecksResults";
import TriageEditor from "../TriageEditor/TriageEditor";
import DismantlingBayEditor from "../DismantlingBayEditor/DismantlingBayEditor";
import CodSettings from "../Cod/CodSettings";
import WasteTrackingSettings from "./WasteTrackingSettings";
import TillSettings from "./TillSettings";
import CallCentreSettings from "./CallCentreSettings";
import LeadImportDiagnostics from "./LeadImport/LeadImportDiagnostics";
import SendingDomainSettings from "./SendingDomain/SendingDomainSettings";
import PartProTradeSettings from "./PartProTrade/PartProTradeSettings";
import ReplaceEbayPolicy from "./ReplaceEbayPolicy/ReplaceEbayPolicy";
import AdminSettingsHome from "./AdminSettingsHome";
import useAdminSections, { findAdminLink } from "./useAdminSections";


export default function AdminMenu() {
  const { section } = useParams();
  const [searchParams] = useSearchParams();
  const hasTriage = CheckFeatureFlag("Triage");
  const hasDefra = CheckFeatureFlag("DEFRA");
  // Exact Betty flag spelling - matches PartProTradeService.FeatureName in the API and the help article gate
  const hasPartProTrade = CheckFeatureFlag("PartPro Trade");
  const hasTill = CheckFeatureFlag("Till");
  const hasCallCentre = CheckFeatureFlag("Call Centre");
  const partHistoryEnabled = usePartHistoryEnabled();
  const { groups, tabs } = useAdminSections();

  if (!section) {
    return <AdminSettingsHome />;
  }

  // Method for returning the correct admin setting page. Gated pages fall back to the landing page
  // as well as being hidden from the cards, or a bookmarked link still reaches them
  const getPanel = () => {
    switch (section) {
      case "admin-credentials":
        return <AdminCredentials />
      case "cod":
        return <CodSettings />
      case "waste-tracking":
        return hasDefra ? <WasteTrackingSettings /> : <AdminSettingsHome />
      case "till":
        return hasTill ? <TillSettings /> : <AdminSettingsHome />
      case "call-centre":
        return hasCallCentre ? <CallCentreSettings /> : <AdminSettingsHome />
      case "lead-import":
        return <LeadImportDiagnostics />
      case "shipping-providers":
        return <AdminShippingProviders />
      case "connections":
        return <AuthConnections />
      case "partpro-trade":
        return hasPartProTrade ? <PartProTradeSettings /> : <AdminSettingsHome />
      case "sending-domain":
        return <SendingDomainSettings />
      case "box-size-editor":
        return <BoxSizeEditor />
      case "end-slate-editor":
        return <EndSlateEditor />
      case "locations":
        return <Locations />
      case "location-groups":
        return <LocationGroups />
      case "part-assembly-editor":
        return <PartAssemblyEditor />
      case "part-category-sub-parts":
        return <PartCategorySubParts />
      case "check-in-editor":
        return <CheckInEditor />
      case "depollution-editor":
        return <DepollutionEditor />
      case "daily-checks-editor":
        return <DailyChecksEditor />
      case "daily-checks":
        return <DailyChecksResults />
      case "triage-editor":
        return hasTriage ? <TriageEditor /> : <AdminSettingsHome />
      case "dismantling-bay-editor":
        return <DismantlingBayEditor />
      case "staff":
        return <Staff />
      case "roles":
        return <Roles />
      case "trucks":
        return <Trucks />
      case "warehouse-locations":
        return <WarehouseLocations />
      case "sites":
        return <Sites />
      case "part-bins":
        return <PartBins />
      case "warranty-manager":
        return <WarrantyManager />
      case "materials":
        return <MaterialManager />
      case "shipping-policies":
        return <ShippingPolicies />
      case "template-policies":
        return <TemplatePolicies />
      case "template-editor":
        return <TemplateEditor />
      case "shopify-shipping-policy-sync":
        return <ShopifyShippingPolicySync />
      case "listing-rules":
        return <ListingRules />
      case "listing-restrictions":
        return <ListingRestrictions />
      case "relisting-rules":
        return <RelistingRules />
      case "replace-ebay-policy":
        return <ReplaceEbayPolicy />
      case "part-image-settings":
        return <PartImageSettingsEditor />
      case "part-tags":
        return <PartTagEditor />
      case "stock-number-format":
        return <StockNumberFormatEditor />
      case "vat-margin-scheme":
        return <VatMarginSchemeSettings />
      case "part-history-fields":
        return partHistoryEnabled ? <PartHistoryFieldsEditor /> : <AdminSettingsHome />
      default:
        return <AdminSettingsHome />
    }
  };

  // Admin Settings opens on its first tab when none is asked for, so the strip highlights that one
  const tab = section === "admin-credentials" ? (searchParams.get("tab") ?? tabs[0] ?? null) : null;
  const current = findAdminLink(groups, section, tab);

  return (
    <div className="mt-4 bg-white rounded-lg shadow">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 px-4 py-3 sm:px-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          All settings
        </Link>
        {current && (
          <>
            <span className="text-gray-300" aria-hidden="true">/</span>
            <span className="text-sm font-semibold text-gray-900">{current.group.name}</span>
            <nav className="flex flex-wrap gap-2 lg:ml-auto" aria-label={`${current.group.name} settings`}>
              {current.group.links.map((link) => {
                const active = link.key === current.link.key;
                return (
                  <Link
                    key={link.key}
                    to={link.href}
                    aria-current={active ? "page" : undefined}
                    className={classNames(
                      active
                        ? "border-primary-200 bg-primary-50 text-primary-700"
                        : "border-gray-200 bg-white text-gray-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700",
                      "inline-flex items-center rounded-full border px-3 py-1 text-sm transition"
                    )}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </>
        )}
      </div>
      <div className="rounded-b-lg bg-gray-50 py-6 px-4 sm:p-6 lg:pb-8">
        {getPanel()}
      </div>
    </div>
  )

}
